import React, { useState, useEffect } from 'react';
import {
  Card,
  Row,
  Col,
  Typography,
  Tag,
  Table,
  Image,
  Timeline,
  Button,
  Space,
  Descriptions,
  Spin,
  Alert,
  Popconfirm,
  message,
} from 'antd';
import { useParams, useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  UserOutlined,
  CalendarOutlined,
  EnvironmentOutlined,
  PhoneOutlined,
  DownloadOutlined,
  CarOutlined,
  ArrowLeftOutlined,
  CloseOutlined,
  GiftOutlined,
} from '@ant-design/icons';
import { DetailedOrder } from '../../types/types';
import Api from '../../Network';
import { storageUrl } from '../../utils/apiBase';
import './OrderDetailsModal.scss';

const { Title, Text } = Typography;

const OrderDetail: React.FC = () => {
  const { t } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const history = useHistory();
  const [order, setOrder] = useState<DetailedOrder | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [actionLoading, setActionLoading] = useState(false);

  const translateOrderText = (value?: string) => {
    if (!value) return value;
    const normalized = value.trim().toLowerCase();

    if (normalized === 'accepted offer and created order') {
      return t(
        'orders.historyDescriptions.acceptedOfferAndCreatedOrder',
        'Offer accepted and order created'
      );
    }

    if (normalized === 'offer accepted') {
      return t('orders.notesValues.offerAccepted', 'Offer accepted');
    }

    return value;
  };

  // Fetch order details
  const fetchOrderDetails = async (orderId: string) => {
    setLoading(true);
    setError(null);
    try {
      const response: any = await Api.get(`/api/admin/orders/${orderId}`);
      if (response.status && response.data) {
        setOrder(response.data);
      } else {
        setError(t('orders.messages.noOrderFound'));
      }
    } catch (error) {
      console.error('Error fetching order details:', error);
      setError(t('orders.messages.operationFailed'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (id) {
      fetchOrderDetails(id);
    }
  }, [id]);

  const handleBack = () => {
    history.goBack();
  };

  const handleCancelOrder = async () => {
    if (!order) return;

    setActionLoading(true);
    try {
      const response: any = await Api.get(`/api/admin/orders/${order.id}/Canceled`);

      if (response.status) {
        message.success(t('orders.messages.orderCancelled', 'Order cancelled successfully'));
        // Refresh order details
        fetchOrderDetails(id!);
      } else {
        message.error(
          t('orders.messages.orderCancelFailed', 'Failed to cancel order. Please try again.')
        );
      }
    } catch (error: any) {
      message.error(
        t('orders.messages.orderCancelFailed', 'Failed to cancel order. Please try again.')
      );
    } finally {
      setActionLoading(false);
    }
  };

  const shouldShowCancel = () => {
    return (
      order?.status !== 'Cancelled' &&
      order?.status !== 'Declined' &&
      order?.status !== 'Completed' &&
      order?.shipping_status !== 'Delivered'
    );
  };

  const getStatusColor = (status: string) => {
    const statusColors: { [key: string]: string } = {
      approved: 'green',
      pending: 'orange',
      cancelled: 'red',
      declined: 'red',
      draft: 'default',
      completed: 'green',
      processing: 'cyan',
    };
    return statusColors[status.toLowerCase()] || 'default';
  };

  const calculateOrderTotals = (orderItems: any[], orderRef?: DetailedOrder | null) => {
    const offerPriceRaw =
      orderRef?.is_offer && orderRef.offer?.special_price != null
        ? orderRef.offer.special_price
        : null;

    if (offerPriceRaw !== null && String(offerPriceRaw) !== '') {
      const subtotal = parseFloat(String(offerPriceRaw));
      if (!Number.isNaN(subtotal)) {
        const taxAmount = subtotal * (15 / 100);

        const loyaltyDiscount = parseFloat(String((orderRef ?? order)?.loyalty_discount || 0));

        const totalBeforeDiscount = subtotal + taxAmount;
        const total = totalBeforeDiscount - loyaltyDiscount;

        return { subtotal, taxAmount, loyaltyDiscount, totalBeforeDiscount, total };
      }
    }

    // Filter out items with quantity 0 for calculations
    const activeItems = orderItems.filter((item) => (item.quantity || 0) > 0);

    const subtotal = activeItems.reduce((sum, item) => {
      const total = item.total || 0;
      return sum + total;
    }, 0);

    const taxAmount = subtotal * (15 / 100);

    // Get loyalty discount from order (if exists)
    const loyaltyDiscount = parseFloat(String((orderRef ?? order)?.loyalty_discount || 0));

    // Total before discount
    const totalBeforeDiscount = subtotal + taxAmount;
    // Final total after discount
    const total = totalBeforeDiscount - loyaltyDiscount;

    return { subtotal, taxAmount, loyaltyDiscount, totalBeforeDiscount, total };
  };

  if (loading) {
    return (
      <div className="order-detail-page" style={{ padding: '24px' }}>
        <div style={{ textAlign: 'center', padding: '50px' }}>
          <Spin size="large" />
          <div style={{ marginTop: '16px' }}>{t('common.loading')}</div>
        </div>
      </div>
    );
  }

  if (error || !order) {
    return (
      <div className="order-detail-page" style={{ padding: '24px' }}>
        <Alert
          message={t('common.error')}
          description={error || t('orders.messages.noOrderFound')}
          type="error"
          showIcon
          style={{ margin: '24px' }}
        />
      </div>
    );
  }

  const { subtotal, loyaltyDiscount } = calculateOrderTotals(order.order_items || [], order);

  // Calculate subtotal after discount (if discount exists)
  const subtotalAfterDiscount = loyaltyDiscount > 0 ? subtotal - loyaltyDiscount : null;

  // Calculate tax amount: if there's a discount, calculate tax on subtotalAfterDiscount, otherwise on subtotal
  const baseAmountForTax = subtotalAfterDiscount !== null ? subtotalAfterDiscount : subtotal;
  const taxAmount = baseAmountForTax * (15 / 100);
  const totalBeforeDiscount = baseAmountForTax + taxAmount;

  const orderItemsColumns = [
    {
      title: t('orders.orderDetails.product'),
      dataIndex: ['product', 'name_ar'],
      key: 'product',
      'data-label': t('orders.orderDetails.product'),
      render: (name: string, record: any) => (
        <div className="product-info">
          <div className="product-name">{name}</div>
          {record.product && (
            <Text type="secondary" className="product-name-en">
              {record.product.name_en}
            </Text>
          )}
        </div>
      ),
    },
    {
      title: t('orders.orderDetails.quantity'),
      dataIndex: 'quantity',
      key: 'quantity',
      width: 120,
      'data-label': t('orders.orderDetails.quantity'),
      render: (quantity: string) => (
        <Text strong className="quantity-text">
          {parseFloat(quantity).toFixed(2)}
        </Text>
      ),
    },
    {
      title: t('orders.orderDetails.unitPrice'),
      dataIndex: 'unit_price',
      key: 'unit_price',
      width: 140,
      'data-label': t('orders.orderDetails.unitPrice'),
      render: (price: string) => (
        <Text className="price-text">
          {parseFloat(price).toFixed(2)} {t('common.SAR')}
        </Text>
      ),
    },
    {
      title: t('orders.orderDetails.tax'),
      dataIndex: 'tax_percent',
      key: 'tax_percent',
      width: 80,
      'data-label': t('orders.orderDetails.tax'),
      render: (tax: string) => <Text className="tax-text">{tax}%</Text>,
    },
    {
      title: t('orders.orderDetails.total'),
      dataIndex: 'total',
      key: 'total',
      width: 120,
      'data-label': t('orders.orderDetails.total'),
      render: (itemTotal: number) => (
        <Text strong className="total-text">
          {itemTotal.toFixed(2)} {t('common.SAR')}
        </Text>
      ),
    },
  ];

  return (
    <div className="order-detail-page" style={{ padding: '24px' }}>
      {/* Header */}
      <div style={{ marginBottom: '24px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <Button icon={<ArrowLeftOutlined />} onClick={handleBack} style={{ marginBottom: 16 }}>
              {t('common.back')}
            </Button>
            <Title level={2} style={{ margin: 0 }}>
              {t('orders.orderDetails.title')} - {order.reference}
            </Title>
          </div>
          {shouldShowCancel() && (
            <Popconfirm
              title={t(
                'orders.actions.confirmCancel',
                'Are you sure you want to cancel this order?'
              )}
              onConfirm={handleCancelOrder}
              okText={t('common.yes')}
              cancelText={t('common.no')}
            >
              <Button danger icon={<CloseOutlined />} loading={actionLoading}>
                {t('orders.actions.cancel', 'Cancel Order')}
              </Button>
            </Popconfirm>
          )}
        </div>
      </div>

      {/* Order Header */}
      <Card
        className={`order-header-card ${order.is_offer ? 'order-header-card--offer' : ''}`}
        style={{ marginBottom: '24px' }}
      >
        <Row gutter={[24, 16]}>
          <Col xs={24} sm={12}>
            <Descriptions column={1} size="small" className="order-info">
              <Descriptions.Item label={t('orders.columns.reference')}>
                <Space size={8} wrap>
                  <Text code strong className="reference-text">
                    {order.reference}
                  </Text>
                  {order.is_offer && (
                    <Tag className="offer-order-badge">
                      <GiftOutlined className="offer-order-badge__icon" />
                      {t('orders.labels.offerOrder', 'Offer Order')}
                    </Tag>
                  )}
                </Space>
              </Descriptions.Item>
              <Descriptions.Item label={t('orders.columns.status')}>
                {order.status === 'Approved' && order.shipping_status === 'Delivered' ? (
                  <Tag color="green" className="status-tag delivered">
                    ✅ {t('orders.status.delivered', 'تم التسليم')}
                  </Tag>
                ) : (
                  <Tag color={getStatusColor(order.status)} className="status-tag">
                    {t(`orders.status.${order.status.toLowerCase()}`, order.status)}
                  </Tag>
                )}
              </Descriptions.Item>
              <Descriptions.Item label={t('orders.columns.location')}>
                <div className="location-info">
                  <EnvironmentOutlined /> {order.location}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label={t('orders.columns.createdAt')}>
                <div className="date-info">
                  <CalendarOutlined /> {new Date(order.created_at).toLocaleString()}
                </div>
              </Descriptions.Item>
            </Descriptions>
          </Col>
          <Col xs={24} sm={12}>
            <Descriptions column={1} size="small" className="order-info">
              <Descriptions.Item label={t('orders.columns.customer')}>
                <div className="customer-info">
                  <UserOutlined /> {order.customer?.name || t('common.noData')}
                </div>
              </Descriptions.Item>
              <Descriptions.Item label={t('orders.columns.phone')}>
                <div className="phone-info">
                  <PhoneOutlined /> {order.customer?.phone_number || t('common.noData')}
                </div>
              </Descriptions.Item>
              {order.driver && (
                <Descriptions.Item label={t('orders.columns.driver')}>
                  <div className="driver-info">
                    <CarOutlined /> {order.driver.name || t('common.noData')}
                  </div>
                </Descriptions.Item>
              )}
              {order.invoice_reference && (
                <Descriptions.Item label={t('orders.columns.invoiceReference')}>
                  <Text code>{order.invoice_reference}</Text>
                </Descriptions.Item>
              )}
            </Descriptions>
          </Col>
        </Row>
      </Card>

      {/* Order Items */}
      <Card
        title={t('orders.orderDetails.orderItems')}
        className="order-items-card"
        style={{ marginBottom: '24px' }}
      >
        <Table
          columns={orderItemsColumns}
          dataSource={order.order_items || []}
          rowKey="id"
          pagination={false}
          size="small"
          className="order-items-table"
          scroll={{ x: 600 }}
          summary={() => (
            <Table.Summary fixed>
              {/* 1. الإجمالي */}
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={4}>
                  <Text strong>{t('orders.orderDetails.subtotal', 'الإجمالي')}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <Text strong>
                    {subtotal.toFixed(2)} {t('common.SAR')}
                  </Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>

              {/* 2. خصم النقاط */}
              {loyaltyDiscount > 0 && (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={4}>
                    <Text strong style={{ color: '#52c41a' }} className="points-discount-text">
                      {t('orders.orderDetails.pointsDiscount', 'خصم النقاط')}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    <Text strong style={{ color: '#52c41a' }}>
                      -{loyaltyDiscount.toFixed(2)} {t('common.SAR')}
                    </Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}

              {/* 3. الإجمالي بعد الخصم */}
              {loyaltyDiscount > 0 && subtotalAfterDiscount !== null && (
                <Table.Summary.Row>
                  <Table.Summary.Cell index={0} colSpan={4}>
                    <Text strong>
                      {t('orders.orderDetails.totalAfterDiscount', 'الإجمالي بعد الخصم')}
                    </Text>
                  </Table.Summary.Cell>
                  <Table.Summary.Cell index={1}>
                    <Text strong>
                      {subtotalAfterDiscount.toFixed(2)} {t('common.SAR')}
                    </Text>
                  </Table.Summary.Cell>
                </Table.Summary.Row>
              )}

              {/* 4. قيمة الضريبة */}
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={4}>
                  <Text strong>{t('orders.orderDetails.taxAmount', 'قيمة الضريبة')}</Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <Text strong>
                    {taxAmount.toFixed(2)} {t('common.SAR')}
                  </Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>

              {/* 5. الإجمالي شامل الضريبة */}
              <Table.Summary.Row>
                <Table.Summary.Cell index={0} colSpan={4}>
                  <Text strong>
                    {t('orders.orderDetails.totalWithTax', 'الإجمالي شامل الضريبة')}
                  </Text>
                </Table.Summary.Cell>
                <Table.Summary.Cell index={1}>
                  <Text strong>
                    {totalBeforeDiscount.toFixed(2)} {t('common.SAR')}
                  </Text>
                </Table.Summary.Cell>
              </Table.Summary.Row>
            </Table.Summary>
          )}
        />
      </Card>

      {/* Order History */}
      {order.order_history && order.order_history.length > 0 && (
        <Card title={t('orders.orderDetails.orderHistory')} style={{ marginBottom: '24px' }}>
          <Timeline>
            {order.order_history.map((history, index) => (
              <Timeline.Item key={index}>
                <div>
                  <Text strong>{history.event || history.event_title}</Text>
                  <br />
                  <Text type="secondary">{new Date(history.created_at).toLocaleString()}</Text>
                  {history.description && (
                    <div style={{ marginTop: '8px' }}>
                      <Text>{translateOrderText(history.description)}</Text>
                    </div>
                  )}
                </div>
              </Timeline.Item>
            ))}
          </Timeline>
        </Card>
      )}

      {/* Additional Information */}
      <Row gutter={[16, 16]}>
        {/* Delivery Document */}
        {order.delivery_document_url && (
          <Col span={12}>
            <Card title={t('orders.orderDetails.deliveryDocument')} size="small">
              <Space direction="vertical" size="small">
                <Button
                  type="primary"
                  icon={<DownloadOutlined />}
                  href={storageUrl(order.delivery_document_url) || undefined}
                  target="_blank"
                  download
                >
                  {t('orders.downloadDocument')}
                </Button>
                <Button
                  size="small"
                  href={storageUrl(order.delivery_document_url) || undefined}
                  target="_blank"
                >
                  {t('orders.viewDocument', 'View Document')}
                </Button>
              </Space>
            </Card>
          </Col>
        )}

        {/* Confirmation Images */}
        {order.confirmation_images && order.confirmation_images.length > 0 && (
          <Col span={12}>
            <Card title={t('orders.orderDetails.confirmationImages')} size="small">
              <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                {order.confirmation_images.map((image, index) => (
                  <Image
                    key={index}
                    width={80}
                    height={80}
                    src={storageUrl(image) || ''}
                    alt={t('orders.orderDetails.confirmationImages') + ` ${index + 1}`}
                    style={{ objectFit: 'cover' }}
                  />
                ))}
              </div>
            </Card>
          </Col>
        )}
      </Row>

      {/* Decline Reason */}
      {order.decline_reason && (
        <Card title={t('orders.orderDetails.declineReason')} style={{ marginTop: '24px' }}>
          <Text type="danger">{order.decline_reason}</Text>
        </Card>
      )}

      {/* Notes */}
      {order.notes && (
        <Card title={t('orders.orderDetails.notes')} style={{ marginTop: '24px' }}>
          <Text>{translateOrderText(order.notes)}</Text>
        </Card>
      )}
    </div>
  );
};

export default OrderDetail;
